import { GeneralInputProps } from '@/components/GeneralInputs/type';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  DATE_FORMAT,
  DATE_FORMAT_MONTH,
  FormDatepicker,
  FormDatepickerProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

type GeneralInputDateType = {
  dateFormatType: 'year_month_day' | 'year_month';
};

export type DateTypeProps<TFieldValues extends FieldValues> =
  FormDatepickerProps<TFieldValues>;

type DateTypeInputProps<TFieldValues extends FieldValues> =
  DateTypeProps<TFieldValues> & GeneralInputProps & GeneralInputDateType;

const InnerDateTypeInput = <TFieldValues extends FieldValues>({
  control,
  inputId,
  rules,
  name,
  dateFormatType,
  placeholder,
}: DateTypeInputProps<TFieldValues>) => {
  const containerRef = useGlobalContainerRef();

  const datepickerProps = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      placeholder: placeholder,
      ...(dateFormatType === 'year_month' && {
        picker: 'month' as const,
      }),
      inputReadOnly: false,
      getPopupContainer: containerRef,
    } satisfies FormDatepickerProps<TFieldValues>['datepickerProps'];
  }, [placeholder, dateFormatType, containerRef]);

  return (
    <FormDatepicker<TFieldValues>
      control={control}
      inputId={inputId}
      rules={rules}
      name={name}
      single
      datepickerProps={datepickerProps}
      format={dateFormatType === 'year_month' ? DATE_FORMAT_MONTH : DATE_FORMAT}
    />
  );
};
InnerDateTypeInput.displayName = 'InnerDateTypeInput';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const DateTypeInput = memo(
  InnerDateTypeInput
) as typeof InnerDateTypeInput;
DateTypeInput.displayName = 'DateTypeInput';
